import type React from "react";
import type { AidvisoraModuleIconKey, AidvisoraQuickIconKey } from "@/lib/assets/aidvisora-icons";
import {
  Calendar,
  FileText,
  Handshake,
  Landmark,
  LayoutGrid,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  PieChart,
  Server,
  ShieldCheck,
  Wallet,
} from "lucide-react";

export type CoverageBadge = "Dostupné" | "Připravujeme" | "Ve výhledu";

export type CoverageLogo = { src: string; alt: string };

export type CoverageItem = {
  id: number;
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  moduleKey?: AidvisoraModuleIconKey;
  quickIconKey?: AidvisoraQuickIconKey;
  badge: CoverageBadge;
  bullets?: readonly string[];
  lead?: string;
  logoSrc?: string;
  logoAlt?: string;
  logos?: readonly CoverageLogo[];
};

export const COVERAGE_BADGE_CLASS: Record<CoverageBadge, string> = {
  Dostupné: "border-emerald-400/20 bg-emerald-400/10 text-emerald-400",
  Připravujeme: "border-amber-400/20 bg-amber-400/10 text-amber-400",
  "Ve výhledu": "border-sky-400/20 bg-sky-400/10 text-sky-300",
};

export const COVERAGE_ITEMS: readonly CoverageItem[] = [
  {
    id: 1,
    title: "Google Kalendář, Gmail a Drive",
    icon: Calendar,
    badge: "Dostupné",
    logos: [
      { src: "/logos/google-calendar.svg", alt: "Google Kalendář" },
      { src: "/logos/gmail.svg", alt: "Gmail" },
      { src: "/logos/google-drive.svg", alt: "Google Disk" },
    ],
    bullets: [
      "sync kalendáře podle nastavení a tarifu",
      "Gmail a komunikace v kontextu klienta",
      "Google Disk pro dokumenty a podklady podle tarifu",
    ],
  },
  {
    id: 2,
    title: "Dokumenty a AI Review smluv",
    icon: FileText,
    moduleKey: "documents",
    badge: "Dostupné",
    bullets: [
      "nahrání PDF a podkladů ke klientovi",
      "AI Review pro extrakci polí z pojistných smluv (dle tarifu)",
      "výstup je interní podklad ke kontrole poradcem",
    ],
  },
  {
    id: 3,
    title: "Klientská zóna",
    icon: PieChart,
    moduleKey: "clientRequests",
    badge: "Dostupné",
    bullets: [
      "dokumenty a požadavky u klienta",
      "chat s poradcem podle tarifu",
      "méně příloh roztroušených v e-mailech",
    ],
  },
  {
    id: 4,
    title: "Servisní e-maily a šablony",
    icon: Mail,
    moduleKey: "emailCampaigns",
    badge: "Dostupné",
    bullets: [
      "servisní šablony a rozesílky pro klienty",
      "dostupnost dle tarifu a nastavení workspace",
      "souhlas se zpracováním pro marketingové e-mail řešíte vůči klientovi",
    ],
  },
  {
    id: 5,
    title: "Kalendář a veřejné rezervace",
    icon: Calendar,
    moduleKey: "calendar",
    badge: "Dostupné",
    bullets: [
      "interní kalendář s klienty, úkoly a událostmi",
      "veřejná rezervační stránka s odkazem `/rezervace/…`",
      "připomínky k událostem dle nastavení",
    ],
  },
  {
    id: 6,
    title: "Finanční analýzy a kalkulačky",
    icon: Wallet,
    moduleKey: "financialPlanning",
    badge: "Dostupné",
    bullets: [
      "wizard finanční analýzy v CRM",
      "orientační kalkulačky jako ilustrativní podklad",
      "prezentační a PDF výstupy dál ladíme podle rozsahu modulu a tarifu",
    ],
  },
  {
    id: 16,
    title: "Aida — práce s podklady",
    icon: MessageSquare,
    moduleKey: "aida",
    badge: "Dostupné",
    bullets: [
      "asistent v kontextu klienta podle tarifu",
      "odpovědi a práce s podklady jako interní pracovní podklad pro poradce",
      "čtení PDF a vícekrokové scénáře dle tarifu a nastavení",
    ],
  },
  {
    id: 7,
    title: "CRM, produkce, importy a upozornění",
    icon: LayoutGrid,
    moduleKey: "production",
    badge: "Dostupné",
    bullets: [
      "úkoly a požadavky vázané na klienta",
      "produkce / pipeline a týmový přehled dle tarifu",
      "importy a hromadné operace na datech",
      "PDF výstupy, role v týmu a notifikace dle tarifu a nastavení",
    ],
  },
  {
    id: 8,
    title: "Přihlášení přes BankID",
    icon: ShieldCheck,
    badge: "Připravujeme",
    logoSrc: "/logos/bankid-logo.png",
    logoAlt: "BankID",
    bullets: [
      "ověřená identita pro vstup do klientské zóny",
      "méně hesel pro klienta při práci s podklady",
      "řešení ve spolupráci s dostupnými službami BankID",
    ],
  },
  {
    id: 9,
    title: "Firemní e-mail přes IMAP/SMTP",
    icon: Server,
    badge: "Připravujeme",
    lead: "Připravujeme propojení firemních schránek mimo Gmail — například Roundcube, vlastní doména nebo firemní hosting přes IMAP/SMTP.",
  },
  {
    id: 10,
    title: "Hlas na text v Aidě",
    icon: Mic,
    quickIconKey: "quickVoice",
    badge: "Dostupné",
    lead: "Poradce může nadiktovat poznámku nebo dotaz hlasem, převést jej na text a dál s ním pracovat v Aidě.",
  },
  {
    id: 11,
    title: "Hypoteční průběh případu",
    icon: Landmark,
    badge: "Dostupné",
    bullets: [
      "poradce zakládá hypotéční případ u klienta v CRM",
      "klient vidí fáze, stav kroků a další krok v klientské zóně",
      "dokumenty a požadavky k jednotlivým krokům podle nastavení",
    ],
  },
  {
    id: 12,
    title: "ARES a údaje o subjektech",
    icon: Landmark,
    badge: "Připravujeme",
    lead: "Automatické doplnění a ověření veřejných údajů o firmách a OSVČ podle IČO.",
  },
  {
    id: 13,
    title: "Katastr a nemovitostní podklady",
    icon: MapPin,
    badge: "Připravujeme",
    lead: "U hypotečních případů chceme usnadnit práci s podklady k nemovitosti, zástavou, vkladem a stavem řízení. Rozsah napojení bude záviset na dostupných službách ČÚZK.",
  },
  {
    id: 14,
    title: "Partnerská datová napojení",
    icon: Handshake,
    badge: "Ve výhledu",
    lead: "Do budoucna počítáme s importy a datovými napojeními na banky, pojišťovny a investiční instituce tam, kde to bude technicky a smluvně možné.",
  },
] as const;
